<script setup>

import { ref, computed, watch, provide } from 'vue';
import Dialog from 'primevue/dialog';
import Checkbox from 'primevue/checkbox';
import TriStateCheckbox from 'primevue/tristatecheckbox';
import MultiSelect from 'primevue/multiselect';
import SelecteurCouleur from './SelecteurCouleur.vue';
import {
	getMiseEnFormeElement } from '@/scripts/global.js';

const listeBordures = [
    {indice: 0, libelle: "Haut"},
    {indice: 1, libelle: "Droite"},
    {indice: 2, libelle: "Bas"},
    {indice: 3, libelle: "Gauche"}
];

const listeAngles = [
    {indice: 0, libelle: "En haut à gauche"},
    {indice: 1, libelle: "En haut à droite"},
    {indice: 2, libelle: "En bas à droite"},
    {indice: 3, libelle: "En bas à gauche"}
];

const { elements } = defineProps({
    elements: Array
});

const visible = defineModel('visible', {
    type: Boolean
});

const emit = defineEmits(['modifMiseEnForme']);

const taillePolice = ref(null);
const couleurPolice = ref(null);
const gras = ref(null);
const italique = ref(null);
const souligne = ref(null);
const couleurFond = ref(null);
const epaisseursBordures = ref(null);
const couleursBordures = ref(null);
const arrondisBordures = ref(null);

const borduresSelectionnees = ref(null);
const epaisseurBorduresSelectionnees = ref(null);
const couleurBorduresSelectionnees = ref(null);

const anglesSelectionnes = ref(null);
const arrondiAnglesSelectionnes = ref(null);

const miseEnFormeIni = computed(() => {
    if (!visible.value) {
        return {};
    }
    const miseEnFormeIni = {};
    if (elements == null) {
        return miseEnFormeIni;
    }
    for (let i = 0; i < elements.length; i++) {
        let mefElement = getMiseEnFormeElement(elements[i]);
        for (let propriete of Object.keys(mefElement)) {
            let valeurPropriete = convertirValeurCss(propriete, mefElement[propriete]);
            if (Object.keys(miseEnFormeIni).includes(propriete)) {
                if (estProprieteVecteur(propriete)) {
                    for (let indice = 0; indice < valeurPropriete.length; indice++) {
                        let dejaPresent = false;
                        for (let valeurRecensee of miseEnFormeIni[propriete][indice]) {
                            if (comparerValeursPropriete(valeurRecensee, valeurPropriete[indice])) {
                                dejaPresent = true;
                                break;
                            }
                        }
                        if (!dejaPresent) {
                            miseEnFormeIni[propriete][indice].push(valeurPropriete[indice]);
                        }
                    }
                } else {
                    let dejaPresent = false;
                    for (let valeurRecensee of miseEnFormeIni[propriete]) {
                        if (comparerValeursPropriete(valeurRecensee, valeurPropriete)) {
                            dejaPresent = true;
                            break;
                        }
                    }
                    if (!dejaPresent) {
                        miseEnFormeIni[propriete].push(valeurPropriete);
                    }
                }
            } else {
                if (estProprieteVecteur(propriete)) {
                    miseEnFormeIni[propriete] = [];
                    for (let indice = 0; indice < valeurPropriete.length; indice++) {
                        miseEnFormeIni[propriete][indice] = Array(i).fill(null);
                        miseEnFormeIni[propriete][indice].push(valeurPropriete[indice]);
                    }
                } else {
                    miseEnFormeIni[propriete] = Array(i).fill(null);
                    miseEnFormeIni[propriete].push(valeurPropriete);
                }
            }
        }
        for (let propriete of Object.keys(miseEnFormeIni)) {
            if (!Object.keys(mefElement).includes(propriete)) {
                if (estProprieteVecteur(propriete)) {
                    for (let indice = 0; indice < miseEnFormeIni[propriete].length; indice++) {
                        if (!miseEnFormeIni[propriete][indice].includes(null)) {
                            miseEnFormeIni[propriete][indice].push(null);
                        }
                    }
                } else {
                    if (!miseEnFormeIni[propriete].includes(null)) {
                        miseEnFormeIni[propriete].push(null);
                    }
                }
            }
        }
    }
    return miseEnFormeIni;
});

watch(() => miseEnFormeIni.value, (mef) => {
    taillePolice.value = mef.fontSize != null 
        ? mef.fontSize.length == 1
            ? mef.fontSize[0]
            : null 
        : getValeurParDefaut('fontSize');
    couleurPolice.value = mef.color != null
        ? mef.color.length == 1
            ? mef.color[0]
            : 'multiple'
        : getValeurParDefaut('color');
    gras.value = mef.fontWeight != null 
        ? mef.fontWeight.length == 1
            ? mef.fontWeight[0]
            : null
        : getValeurParDefaut('fontWeight');
    italique.value = mef.fontStyle != null 
        ? mef.fontStyle.length == 1
            ? mef.fontStyle[0]
            : null
        : getValeurParDefaut('fontStyle');
    souligne.value = mef.textDecoration != null 
        ? mef.textDecoration.length == 1
            ? mef.textDecoration[0]
            : null
        : getValeurParDefaut('textDecoration');
    couleurFond.value = mef.backgroundColor != null
        ? mef.backgroundColor.length == 1
            ? mef.backgroundColor[0]
            : 'multiple'
        : getValeurParDefaut('backgroundColor');
    epaisseursBordures.value = mef.borderWidth != null
        ? mef.borderWidth.map(epaisseursCote => epaisseursCote.length == 1 ? epaisseursCote[0] : null)
        : getValeurParDefaut('borderWidth');
    couleursBordures.value = mef.borderColor != null
        ? mef.borderColor.map(couleursCote => couleursCote.length == 1 ? couleursCote[0] : 'multiple')
        : getValeurParDefaut('borderColor');
    arrondisBordures.value = mef.borderRadius != null
        ? mef.borderRadius.map(arrondisAngle => arrondisAngle.length == 1 ? arrondisAngle[0] : null)
        : getValeurParDefaut('borderRadius');

    borduresSelectionnees.value = listeBordures;
    epaisseurBorduresSelectionnees.value = getValeurProprieteCommune(epaisseursBordures.value, borduresSelectionnees.value, null);
    couleurBorduresSelectionnees.value = getValeurProprieteCommune(couleursBordures.value, borduresSelectionnees.value, 'multiple');

    anglesSelectionnes.value = listeAngles;
    arrondiAnglesSelectionnes.value = getValeurProprieteCommune(arrondisBordures.value, anglesSelectionnes.value, null);
});

const miseEnFormeModifiee = computed(() => {
    return {
        fontSize: taillePolice.value != null ? `${taillePolice.value}px` : null,
        color: couleurPolice.value != null ? convertirCouleurEnRgb(couleurPolice.value) : null,
        fontWeight: gras.value ? 'bold' : null,
        fontStyle: italique.value ? 'italic' : null,
        textDecoration: souligne.value ? 'underline' : null,
        backgroundColor: couleurFond.value != null ? convertirCouleurEnRgb(couleurFond.value) : null,
        borderWidth: epaisseursBordures.value != null ? epaisseursBordures.value.map(epaisseur => convertirEntierVersPixels(epaisseur)).join(' ') : null,
        borderColor: couleursBordures.value != null ? couleursBordures.value.map(couleur => convertirCouleurEnRgb(couleur)).join(' ') : null,
        borderRadius: arrondisBordures.value != null ? arrondisBordures.value.map(arrondi => convertirEntierVersPixels(arrondi)).join(' ') : null
    };
});

const miseEnFormeExemple = computed(() => {
    return {
        fontSize: miseEnFormeModifiee.value.fontSize,
        color: miseEnFormeModifiee.value.color,
        fontWeight: miseEnFormeModifiee.value.fontWeight,
        fontStyle: miseEnFormeModifiee.value.fontStyle,
        textDecoration: miseEnFormeModifiee.value.textDecoration,
        backgroundColor: miseEnFormeModifiee.value.backgroundColor,
        borderWidth: miseEnFormeModifiee.value.borderWidth != null ? miseEnFormeModifiee.value.borderWidth.replaceAll('null', '0px') : null,
        borderColor: miseEnFormeModifiee.value.borderColor != null ? miseEnFormeModifiee.value.borderColor.replaceAll('null', 'rgb(0, 0, 0)') : null,
        borderRadius: miseEnFormeModifiee.value.borderRadius != null ? miseEnFormeModifiee.value.borderRadius.replaceAll('null', '0px') : null
    };
});

watch(() => borduresSelectionnees.value, (selection) => {
    epaisseurBorduresSelectionnees.value = getValeurProprieteCommune(epaisseursBordures.value, selection, null);
    couleurBorduresSelectionnees.value = getValeurProprieteCommune(couleursBordures.value, selection, 'multiple');
});

watch(() => anglesSelectionnes.value, (selection) => {
    arrondiAnglesSelectionnes.value = getValeurProprieteCommune(arrondisBordures.value, selection, null);
});

watch(() => epaisseurBorduresSelectionnees.value, (epaisseur) => {
    if (epaisseur != null) {
        modifierValeurPropriete(epaisseursBordures.value, borduresSelectionnees.value.map(bordure => bordure.indice), epaisseur);
    }
});

watch(() => couleurBorduresSelectionnees.value, (couleur) => {
    if (couleur != null) {
        modifierValeurPropriete(couleursBordures.value, borduresSelectionnees.value.map(bordure => bordure.indice), couleur);
    }
});

watch(() => arrondiAnglesSelectionnes.value, (arrondi) => {
    if (arrondi != null) {
        modifierValeurPropriete(arrondisBordures.value, anglesSelectionnes.value.map(angle => angle.indice), arrondi);
    }
});

function convertirValeurCss(propriete, valeur) {
    switch(propriete) {
        case 'fontSize':
            return parseInt(valeur.replace("px", ""));
        case 'color':
        case 'backgroundColor':
            return convertirRgbEnCouleur(valeur);
        case 'fontWeight': 
            return (valeur == 'bold');
        case 'fontStyle':
            return (valeur == 'italic');
        case 'textDecoration':
            return (valeur == 'underline');
        case 'borderWidth':
        case 'borderRadius':
            return developperValeursPropriete(convertirTextePixelsVersEntiers(valeur));
        case 'borderColor':
            return developperValeursPropriete(convertirTexteRgbEnCouleurs(valeur));
    }
}

function convertirEnValeurCss(propriete, valeur) {
    if (valeur == null) {
        return null;
    }
    switch(propriete) {
        case 'fontSize':
            return `${valeur}px`;
        case 'color':
        case 'backgroundColor':
            return convertirCouleurEnRgb(valeur);
        case 'fontWeight': 
            return valeur ? 'bold' : null;
        case 'fontStyle':
            return valeur ? 'italic' : null;
        case 'textDecoration':
            return valeur ? 'underline' : null;
        case 'borderWidth':
        case 'borderRadius':
            return valeur.map(element => convertirEntierVersPixels(element)).join(' ');
        case 'borderColor':
            return valeur.map(element => convertirCouleurEnRgb(element)).join(' ');
    }
}

function estProprieteVecteur(propriete) {
    switch(propriete) {
        case 'fontSize':
        case 'color':
        case 'backgroundColor':
        case 'fontWeight': 
        case 'fontStyle':
        case 'textDecoration':
            return false;
        case 'borderWidth':
        case 'borderRadius':
        case 'borderColor':
            return true;
    }
}

function valider() {
    const miseEnFormeGenerique = {};
    for (let propriete of Object.keys(miseEnFormeModifiee.value)) {
        if (miseEnFormeModifiee.value[propriete] != null || miseEnFormeIni.value[propriete] != null) {
            if (miseEnFormeModifiee.value[propriete] != getValeurCssParDefaut(propriete)) {
                miseEnFormeGenerique[propriete] = miseEnFormeModifiee.value[propriete];
            } else {
                miseEnFormeGenerique[propriete] = null;
            }
        }
    }
    emit('modifMiseEnForme', {miseEnForme: miseEnFormeGenerique, transformation: appliquerMiseEnFormeGenerique});
}

function getValeurCssParDefaut(propriete) {
    return convertirEnValeurCss(propriete, getValeurParDefaut(propriete));
}

function getValeurParDefaut(propriete) {
    switch(propriete) {
        case 'fontSize':
            return 10;
        case 'color':
            return {r: 0, g: 0, b: 0};
        case 'backgroundColor':
        return null;
        case 'fontWeight': 
            return false;
        case 'fontStyle':
            return false;
        case 'textDecoration':
            return false;
        case 'borderWidth':
            return [0, 0, 0, 0];
        case 'borderRadius':
            return [0, 0, 0, 0];
        case 'borderColor':
            return [{r: 0, g: 0, b: 0}, {r: 0, g: 0, b: 0}, {r: 0, g: 0, b: 0}, {r: 0, g: 0, b: 0}];
    }
}

function convertirRgbEnCouleur(texteRgb) {
    const matcher = texteRgb.match(/^rgb\((\d+), (\d+), (\d+)\)$/i);
    if(matcher != null) {
        return {
            r: parseInt(matcher[1]), 
            g: parseInt(matcher[2]), 
            b: parseInt(matcher[3])
        };
    } else {
        return null;
    }
}

function reduireValeursPropriete(valeursPropriete) {
    if (valeursPropriete.length > 1) {
        const nouvelleTaille = valeursPropriete.length / 2;
        const valeursReduites = Array(nouvelleTaille);
        for (let i = 0; i < nouvelleTaille; i++) {
            if (comparerValeursPropriete(valeursPropriete[i], valeursPropriete[i + nouvelleTaille])) {
                valeursReduites[i] = valeursPropriete[i];
            } else {
                return valeursPropriete;
            }
        }
        return reduireValeursPropriete(valeursReduites);
    } else {
        return valeursPropriete;
    }
}

function developperValeursPropriete(valeursPropriete) {
    if (valeursPropriete.length < 4) {
        const nouvelleTaille = valeursPropriete.length * 2;
        const valeursDeveloppees = Array(nouvelleTaille);
        for (let i = 0; i < nouvelleTaille; i++) {
            valeursDeveloppees[i] = valeursPropriete[i % valeursPropriete.length];
        }
        return developperValeursPropriete(valeursDeveloppees);
    } else {
        return valeursPropriete;
    }
}

function comparerValeursPropriete(valeur1, valeur2) {
    if (valeur1 == valeur2) {
        return true;
    } else if (valeur1 == null) {
        return false;
    } else if (Object.keys(valeur1).length > 0) {
        for (let propriete of Object.keys(valeur1)) {
            if (valeur1[propriete] != valeur2[propriete]) {
                return false;
            }
        }
        return true;
    } else {
        return false;
    }
}

function getValeurProprieteCommune(valeursPropriete, selection, valeurMultiple) {
    var valeurCommune = null;
    const valeursSelection = selection.map(sel => valeursPropriete[sel.indice]);
    var premiereBordure = true;
    for (var valeur of valeursSelection) {
        if (premiereBordure) {
            valeurCommune = valeur;
            premiereBordure = false;
        } else {
            if (!comparerValeursPropriete(valeur, valeurCommune))
                return valeurMultiple;
        }
    }
    return valeurCommune;
}

function appliquerMiseEnFormeGenerique(miseEnFormeElement, miseEnFormeGenerique) {
    let nouvelleMiseEnFormeElement = Object.assign({}, miseEnFormeElement);
    for (const propriete of Object.keys(miseEnFormeGenerique)) {
         if (miseEnFormeGenerique[propriete] != null && ['borderWidth', 'borderRadius', 'borderColor'].includes(propriete)) {
            // Cas des propriétés représentées par un vecteur pouvant contenir des 'null'
            let valeursElement = null;
            if (Object.keys(nouvelleMiseEnFormeElement).includes(propriete)) {
                // La mise en forme de l'élément définit déjà cette propriété
                valeursElement = convertirValeurCss(propriete, nouvelleMiseEnFormeElement[propriete]);
            } else {
                valeursElement = getValeurParDefaut(propriete);
            }
            let valeursGeneriques = convertirValeurCss(propriete, miseEnFormeGenerique[propriete]);
            let indice = 0;
            for (let valeur of valeursGeneriques) {
                if (valeur != null) {
                    valeursElement[indice] = valeur;
                }
                indice++;
            }
            valeursElement = reduireValeursPropriete(valeursElement);
            nouvelleMiseEnFormeElement[propriete] = convertirEnValeurCss(propriete, valeursElement);
        } else {
            // Cas simple
            nouvelleMiseEnFormeElement[propriete] = miseEnFormeGenerique[propriete];
        }
    }
    return nouvelleMiseEnFormeElement;
}

function modifierValeurPropriete(valeursPropriete, indices, valeur) {
    let nouvellesValeursPropriete = [0, 1, 2, 3].map(indiceBordure => 
            (indices.includes(indiceBordure) ? valeur : valeursPropriete[indiceBordure]));
    valeursPropriete.length = 0;
    nouvellesValeursPropriete.forEach(valPropriete => valeursPropriete.push(valPropriete));
}

function convertirCouleurEnRgb(couleur) {
    return couleur != null && couleur != 'multiple'
        ? `rgb(${couleur.r}, ${couleur.g}, ${couleur.b})`
        : 'null';
}

function convertirTextePixelsVersEntiers(epaisseursCss) {
    return epaisseursCss.split(' ')
        .map(texteEpaisseur => texteEpaisseur == 'null' ? null : parseInt(texteEpaisseur.replace('px', '')));
}

function convertirEntierVersPixels(entier) {
    return entier != null
        ? `${entier}px`
        : 'null';
}

function convertirTexteRgbEnCouleurs(couleursCss) {
    return couleursCss.replaceAll(', ', ',').split(' ')
        .map(couleurCss => couleurCss.replaceAll(',', ', '))
        .map(couleurCss => convertirRgbEnCouleur(couleurCss));
}

provide('convertirRgbEnCouleur', convertirRgbEnCouleur);
provide('convertirCouleurEnRgb', convertirCouleurEnRgb);

</script>

<template>
    <Dialog 
    v-model:visible="visible" 
    :style="{ width: '50vw' }"
    modal 
    header="Edition de mise en forme">
        <label class="intitule titre-rubrique">Police</label>
        <div class="rubrique">
            <label class="intitule">Taille</label>
            <input type="number" min="1" class="selecteur-nombre" v-model="taillePolice" />
            <label class="intitule">Couleur</label>
            <SelecteurCouleur 
                class="selecteur-couleur"
                v-model:couleur="couleurPolice"
                :couleur-par-defaut="getValeurParDefaut('color')"
                :premiere-couleur-proposee="{r: 40, g: 40, b: 40}" />
            <TriStateCheckbox v-if="miseEnFormeIni.fontWeight === 'multiple'" v-model="gras" />
            <Checkbox v-else v-model="gras" :binary="true" />
            <label class="intitule case-a-cocher" style="font-weight: bold; ">Gras</label>
            <TriStateCheckbox v-if="miseEnFormeIni.fontStyle === 'multiple'" v-model="italique" />
            <Checkbox v-else v-model="italique" :binary="true" />
            <label class="intitule case-a-cocher" style="font-style: italic; ">Italique</label>
            <TriStateCheckbox v-if="miseEnFormeIni.textDecoration === 'multiple'" v-model="souligne" />
            <Checkbox v-else v-model="souligne" :binary="true" />
            <label class="intitule case-a-cocher" style="text-decoration: underline; ">Souligné</label>
        </div>
        <hr>
        <label class="intitule titre-rubrique">Fond</label>
        <div class="rubrique">
            <label class="intitule">Couleur</label>
            <SelecteurCouleur 
                class="selecteur-couleur"
                v-model:couleur="couleurFond"
                :couleur-par-defaut="getValeurParDefaut('backgroundColor')"
                :premiere-couleur-proposee="{r: 230, g: 230, b: 230}" />
        </div>
        <hr>
        <label class="intitule titre-rubrique">Bordures</label>
        <div class="rubrique">
            <label class="intitule">Bordure(s) :</label>
            <MultiSelect 
            class="liste-positions"
            v-model="borduresSelectionnees" 
            :options="listeBordures" 
            optionLabel="libelle" />
        </div>
        <div class="rubrique">
            <label class="intitule">Epaisseur :</label>
            <input type="number" min="0" class="selecteur-nombre" v-model="epaisseurBorduresSelectionnees" />
            <label class="intitule">Couleur</label>
            <SelecteurCouleur 
                class="selecteur-couleur"
                v-model:couleur="couleurBorduresSelectionnees"
                :couleur-par-defaut="getValeurParDefaut('color')"
                :premiere-couleur-proposee="{r: 40, g: 40, b: 40}" />
        </div>
        <hr>
        <label class="intitule titre-rubrique">Arrondis</label>
        <div class="rubrique">
            <label class="intitule">Angle(s) :</label>
            <MultiSelect 
            class="liste-positions"
            v-model="anglesSelectionnes" 
            :options="listeAngles" 
            optionLabel="libelle" />
        </div>
        <div class="rubrique">
            <label class="intitule">Arrondi :</label>
            <input type="number" min="0" class="selecteur-nombre" v-model="arrondiAnglesSelectionnes" />
        </div>
        <hr>
        <label class="intitule titre-rubrique">Aperçu</label>
        <div class="zone-exemple">
            <label class="exemple" :style="miseEnFormeExemple">Texte</label>
        </div>
        <template #footer>
            <button 
                class="bouton fas fa-check" 
                :disabled="false"
                @click="valider()"/>
        </template>
    </Dialog>
</template>

<style scoped>

.intitule {
    margin-right: 5px;
}

.intitule.case-a-cocher {
    margin-left: 5px;
    margin-right: 20px;
    user-select: none;
}

.selecteur-nombre {
    margin-left: 5px;
    margin-right: 20px;
    width: 40px;
}

.titre-rubrique {
    font-weight: bold;
    margin-bottom: 5px;
    font-size: 12px;
}

.rubrique {
    display: flex;
    margin-bottom: 15px;
    align-items: center;
}

.selecteur-couleur {
    margin-right: 20px;
}

.exemple {
    display: block;
    text-align: center;
    padding-top: 2px 10px; 
    height: 50px;
    width: 100%;
    overflow: hidden;
    border-style: solid;
}

.zone-exemple {
    border: 1px solid lightgrey;
    padding: 20px;
}

.liste-positions {
    height: 20px; 
    width: 100%;
}

</style>
